/*
 * Copyright (c) 2026, Gather
 * https://gather.life
 */

import Popper from '@mui/material/Popper/Popper';
import { useMeasure } from '@uidotdev/usehooks';
import {
  type ChangeEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type SyntheticEvent,
  useCallback,
  useMemo,
  useState,
} from 'react';

import { ZIndex } from '@marketplace/gather-ui/theme';

import { type RequiredBodyType, type RequiredMakeModel } from '../../store/catalog-filter.store';
import { type SearchType } from '../../store/catalog-search.stored-state';

import { SearchDropdown } from './dropdown/search-dropdown.component';
import { useSearchDropdown } from './dropdown/search-dropdown.provider';
import { SearchInput } from './search-input/search-input.component';

export type UpdateSearchValueParams =
  | {
      autoSubmit: boolean;
      searchType: Exclude<SearchType, `QUICK_${string}`>;
      searchValue: string;
      selectedFilter: null;
    }
  | {
      autoSubmit: boolean;
      searchType: Extract<SearchType, 'QUICK_BODY_TYPE'>;
      searchValue: string;
      selectedFilter: RequiredBodyType;
    }
  | {
      autoSubmit: boolean;
      searchType: Extract<SearchType, 'QUICK_MAKE'>;
      searchValue: string;
      selectedFilter: RequiredMakeModel;
    };

export type UpdateSearchValueFunc = SearchProps['updateSearchValue'];

type SearchProps = {
  disabled: boolean;
  handleBlur: (event: React.FocusEvent<HTMLElement>) => void;
  handleSubmit: () => void;
  loading: boolean;
  onReset: () => void;
  updateSearchValue: (options: UpdateSearchValueParams) => Promise<void>;
  value: string;
};

/**
 * Manages the all the elements and state required to make the search dropdown
 * function correctly.
 */
export function Search({
  disabled,
  handleBlur,
  handleSubmit,
  loading,
  onReset,
  updateSearchValue,
  value,
}: SearchProps): ReactNode {
  const { closeDropdown, dropdownState, openDropdown } = useSearchDropdown();

  const handleInputChange = useCallback(
    async (changeEvent: ChangeEvent<HTMLInputElement>) => {
      openDropdown();

      await updateSearchValue({
        autoSubmit: false,
        searchType: 'RAW_SEARCH',
        searchValue: changeEvent.target.value,
        selectedFilter: null,
      });
    },
    [openDropdown, updateSearchValue]
  );

  // allows user to use enter button to search/submit
  const handleOnEnterPress = useCallback(
    (keyboardEvent: ReactKeyboardEvent<HTMLInputElement>) => {
      if (keyboardEvent.key === 'Enter') {
        // Prevent the native form submission to avoid double-submit race conditions
        keyboardEvent.preventDefault();

        closeDropdown();

        /**
         * MANUALLY TRIGGER BLUR:
         * When the form submits, the input becomes 'disabled' (loading state).
         * If the input still has focus when it becomes disabled, React/Formik can crash
         * trying to process the forced blur event on a disabled component (error: "reading 'persist'").
         * By blurring manually here, we ensure the blur event finishes cleanly BEFORE
         * the submit/loading state takes over.
         */
        keyboardEvent.currentTarget.blur();

        handleSubmit();
      }
    },
    [closeDropdown, handleSubmit]
  );

  const handleSearchSubmit = useCallback(
    (inputEvent: SyntheticEvent) => {
      inputEvent.stopPropagation();
      closeDropdown();
      handleSubmit();
    },
    [closeDropdown, handleSubmit]
  );

  // This is requried because the Popper component only will accept an element
  // or null and won't accept a function that returns an element or null
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  const [anchorWidthRef, { width: anchorWidth }] = useMeasure();
  const popperStyles = useMemo(
    () => ({
      width: anchorWidth,
      // keep popper below the input wrapper to avoid covering it
      zIndex: ZIndex.dropdown,
    }),
    [anchorWidth]
  );

  // The popper is designed to be placed on top of the search input which
  // since has a higher z-index will appear above it. This is done so that
  // we don't have to manage two different search inputs. The `yOffset` is
  // used to shift the dropdown contents down so that there is room for the
  // search input.
  const [inputHeightRef, { height: yOffset }] = useMeasure();

  return (
    <div>
      <div ref={setAnchorEl}>
        <div ref={anchorWidthRef} />
      </div>
      <SearchInput
        disabled={disabled}
        inputRef={inputHeightRef}
        loading={loading}
        value={value}
        onBlur={handleBlur}
        onChange={handleInputChange}
        onFocus={openDropdown}
        onKeyDown={handleOnEnterPress}
        onReset={onReset}
        onSubmit={handleSearchSubmit}
      />

      {/* keepMounted is required otherwise the collaspe animation functions correctly */}
      <Popper
        disablePortal
        keepMounted
        transition
        anchorEl={anchorEl}
        aria-hidden={dropdownState !== 'opened'}
        id='search-suggestions-dropdown'
        open={dropdownState === 'opened'}
        placement='bottom-end'
        role='listbox'
        sx={popperStyles}
      >
        {({ TransitionProps }) => (
          <SearchDropdown
            transitionProps={TransitionProps}
            updateSearchValue={updateSearchValue}
            value={value}
            yOffset={yOffset}
          />
        )}
      </Popper>
    </div>
  );
}
